import React, { useState } from 'react';
import {
    LayoutDashboard,
    DollarSign,
    Settings,
    LogOut,
    Menu,
    X,
    ClipboardList,
    Bell,
    User
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../hooks/useTheme';
import { useNavigationHistory } from '../hooks/useNavigationHistory';
import ExitConfirmDialog from '../components/ExitConfirmDialog';
import VendorPortalDashboard from '../components/VendorPortalDashboard';
import ServiceTickets from '../components/ServiceTickets';
import InvoiceManagement from '../components/InvoiceManagement';
import UserSettings from '../components/UserSettings';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface VendorPortalProps {
    user: any;
    onLogout: () => void;
}

export default function VendorPortal({ user, onLogout }: VendorPortalProps) {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isDarkMode } = useTheme(user.id);

    // 📱 Navigation History - Enable proper back button behavior for PWA
    const { showExitConfirmDialog, handleConfirmExit, handleCancelExit } = useNavigationHistory({
        activeTab,
        setActiveTab,
        options: {
            defaultTab: 'dashboard',
            preventExit: true,
            showExitDialog: true
        }
    });

    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'jobs', label: 'My Jobs', icon: ClipboardList },
        { id: 'invoices', label: 'Invoices', icon: DollarSign },
        { id: 'settings', label: 'Settings', icon: Settings },
    ];

    // Company info wrapper for Dashboard
    const companyInfo = {
        id: user.companyId || 'default',
        name: user.company || 'OilWellX'
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <VendorPortalDashboard
                        user={user}
                        company={companyInfo}
                        onNavigate={setActiveTab}
                    />
                );
            case 'jobs':
                return (
                    <div className="space-y-6 animate-in fade-in duration-500">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Managed Jobs
                            </h2>
                        </div>
                        <ServiceTickets
                            wells={[]}
                            currentUser={user}
                            isDarkMode={isDarkMode}
                        />
                    </div>
                );
            case 'invoices':
                return <InvoiceManagement currentUser={user} />;
            case 'settings':
                return (
                    <UserSettings
                        user={user}
                        onClose={() => setActiveTab('dashboard')}
                        onSave={() => toast.success('Settings updated')}
                        isDarkMode={isDarkMode}
                    />
                );
            default:
                return <VendorPortalDashboard user={user} company={companyInfo} />;
        }
    };

    return (
        <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'dark bg-slate-950' : 'bg-slate-50'}`}>
            <Toaster position="top-right" theme={isDarkMode ? 'dark' : 'light'} />

            {/* Top Navigation Bar */}
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        {/* Logo & Desktop Nav */}
                        <div className="flex items-center gap-8">
                            <div className="flex items-center gap-2">
                                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-lg text-white shadow-lg shadow-blue-500/20">
                                    <LayoutDashboard className="w-5 h-5" />
                                </div>
                                <span className="font-bold text-xl text-slate-900 dark:text-white hidden sm:block">
                                    Vendor<span className="text-blue-600">Portal</span>
                                </span>
                            </div>

                            {/* Desktop Menu */}
                            <nav className="hidden md:flex items-center gap-1">
                                {menuItems.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => setActiveTab(item.id)}
                                        className={`
                                            flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                                            ${activeTab === item.id
                                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 shadow-sm ring-1 ring-blue-100 dark:ring-blue-800'
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800'
                                            }
                                        `}
                                    >
                                        <item.icon className={`w-4 h-4 ${activeTab === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-500'}`} />
                                        {item.label}
                                    </button>
                                ))}
                            </nav>
                        </div>

                        {/* Right Actions */}
                        <div className="flex items-center gap-4">
                            {/* Mobile Menu Toggle */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>

                            {/* Notifications */}
                            <button className="relative p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors">
                                <Bell className="w-5 h-5" />
                                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-slate-900"></span>
                            </button>

                            {/* User Profile Dropdown */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="relative h-9 w-9 rounded-full ring-2 ring-white dark:ring-slate-800 shadow-sm overflow-hidden p-0">
                                        <div className="h-full w-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                                            {user.name?.charAt(0) || 'V'}
                                        </div>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-56" align="end" forceMount>
                                    <DropdownMenuLabel className="font-normal">
                                        <div className="flex flex-col space-y-1">
                                            <p className="text-sm font-medium leading-none">{user.name}</p>
                                            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                                        <Settings className="mr-2 h-4 w-4" />
                                        <span>Settings</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={onLogout} className="text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        <span>Log out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                        <div className="space-y-1 p-4">
                            {menuItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => {
                                        setActiveTab(item.id);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className={`
                                        w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors
                                        ${activeTab === item.id
                                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                            : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'}
                                    `}
                                >
                                    <item.icon className="w-5 h-5" />
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </header>

            {/* Main Content Area */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {renderContent()}
            </main>

            {/* Exit Confirmation Dialog - shown when user presses back twice */}
            <ExitConfirmDialog
                open={showExitConfirmDialog}
                onConfirm={handleConfirmExit}
                onCancel={handleCancelExit}
            />
        </div>
    );
}
